/**
 * Permissions — the single source of truth shared by API and UI.
 *
 * The database holds the same strings in the `permissions` table, seeded from
 * this list (db/seeds/001_permissions_and_roles.sql). A test asserts the two
 * agree, because a permission that exists in one and not the other fails
 * silently — the UI hides a button that the API would have allowed, or worse,
 * shows one it would refuse.
 *
 * Matrix and rationale: docs/02-roles-and-permissions.md
 */

export const PERMISSIONS = {
  // --- Requests ---
  REQUEST_DRAFT: 'request.draft',
  REQUEST_SUBMIT: 'request.submit',
  REQUEST_VIEW_OWN: 'request.view.own',
  REQUEST_VIEW_DEPARTMENT: 'request.view.department',
  REQUEST_VIEW_SCHOOL: 'request.view.school',
  REQUEST_VIEW_ALL_SCHOOLS: 'request.view.all_schools',
  REQUEST_RECOMMEND: 'request.recommend',
  REQUEST_RETURN: 'request.return',
  REQUEST_APPROVE_SCHOOL: 'request.approve.school',
  REQUEST_APPROVE_ESCALATED: 'request.approve.escalated',
  REQUEST_CANCEL_OWN: 'request.cancel.own',
  REQUEST_CANCEL_ANY: 'request.cancel.any',

  // --- Budgets ---
  BUDGET_VIEW_DEPARTMENT: 'budget.view.department',
  BUDGET_VIEW_SCHOOL: 'budget.view.school',
  BUDGET_VIEW_ALL: 'budget.view.all',
  BUDGET_OVERRIDE: 'budget.override',
  BUDGET_MANAGE: 'budget.manage',

  // --- Purchase orders ---
  PO_VIEW: 'po.view',
  PO_CREATE: 'po.create',
  PO_SEND: 'po.send',
  PO_CANCEL: 'po.cancel',

  // --- Goods receipt ---
  GOODS_RECEIVE: 'goods.receive',

  // --- Invoices ---
  INVOICE_VIEW: 'invoice.view',
  INVOICE_UPLOAD: 'invoice.upload',
  INVOICE_MATCH: 'invoice.match',
  INVOICE_RESOLVE_DISCREPANCY: 'invoice.resolve_discrepancy',
  INVOICE_EXPORT: 'invoice.export',
  INVOICE_MARK_PAID: 'invoice.mark_paid',

  // --- Suppliers ---
  SUPPLIER_VIEW: 'supplier.view',
  SUPPLIER_MANAGE: 'supplier.manage',
  SUPPLIER_APPROVE_NEW: 'supplier.approve_new',
  /** Bank details are the highest-value target in the system. Every view is audited. */
  SUPPLIER_VIEW_BANK: 'supplier.view_bank',

  // --- Reporting ---
  REPORT_VIEW_SCHOOL: 'report.view.school',
  REPORT_VIEW_ALL: 'report.view.all',
  REPORT_EXPORT: 'report.export',

  // --- Overtime -------------------------------------------------------------
  OVERTIME_SUBMIT: 'overtime.submit',
  OVERTIME_VIEW_OWN: 'overtime.view.own',
  OVERTIME_VIEW_DEPARTMENT: 'overtime.view.department',
  OVERTIME_VIEW_SCHOOL: 'overtime.view.school',
  OVERTIME_VIEW_ALL: 'overtime.view.all',
  OVERTIME_APPROVE: 'overtime.approve',
  OVERTIME_EXPORT: 'overtime.export',

  // --- Audit ---
  AUDIT_VIEW: 'audit.view',

  // --- Administration ---
  ADMIN_USERS: 'admin.users',
  ADMIN_ROLES: 'admin.roles',
  /** Group-level buyers only: pooling demand across schools is a group decision. */
  BUYING_COORDINATE: 'buying.coordinate',
  ADMIN_SCHOOLS: 'admin.schools',
  ADMIN_DEPARTMENTS: 'admin.departments',
  ADMIN_APPROVAL_RULES: 'admin.approval_rules',
  ADMIN_SETTINGS: 'admin.settings',
  ADMIN_TEMPLATES: 'admin.templates',
  ADMIN_DEMO_RESET: 'admin.demo_reset',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ALL_PERMISSIONS = Object.values(PERMISSIONS) as Permission[];

/** Role keys seeded by the system. Administrators may create others. */
export const ROLES = {
  TEACHER: 'teacher',
  REQUESTER: 'requester',
  HEAD_OF_DEPARTMENT: 'head_of_department',
  HEAD_TEACHER: 'head_teacher',
  DIRECTOR: 'director',
  SCHOOL_FINANCE: 'school_finance',
  HEAD_OFFICE_FINANCE: 'head_office_finance',
  SYSTEM_ADMINISTRATOR: 'system_administrator',
} as const;

export type RoleKey = (typeof ROLES)[keyof typeof ROLES];

export type RoleScope = 'global' | 'school' | 'department';

/**
 * Permissions School Finance must never hold, at any scope, under any
 * configuration. Asserted in tests and re-checked when roles are edited in the
 * Admin Panel — the brief's strongest rule, so it is enforced rather than
 * documented (docs/02).
 */
export const FORBIDDEN_FOR_SCHOOL_FINANCE: Permission[] = [
  PERMISSIONS.REQUEST_APPROVE_SCHOOL,
  PERMISSIONS.REQUEST_APPROVE_ESCALATED,
];

/** Granting any of these is a control change and is flagged in the audit log. */
export const SENSITIVE_PERMISSIONS: Permission[] = [
  PERMISSIONS.REQUEST_APPROVE_SCHOOL,
  PERMISSIONS.REQUEST_APPROVE_ESCALATED,
  PERMISSIONS.BUDGET_OVERRIDE,
  PERMISSIONS.BUDGET_MANAGE,
  PERMISSIONS.SUPPLIER_APPROVE_NEW,
  PERMISSIONS.SUPPLIER_VIEW_BANK,
  PERMISSIONS.INVOICE_EXPORT,
  PERMISSIONS.INVOICE_RESOLVE_DISCREPANCY,
  PERMISSIONS.OVERTIME_APPROVE,
  PERMISSIONS.OVERTIME_EXPORT,
  PERMISSIONS.ADMIN_USERS,
  PERMISSIONS.ADMIN_ROLES,
  PERMISSIONS.ADMIN_SETTINGS,
  PERMISSIONS.ADMIN_APPROVAL_RULES,
];
